import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  InputAdornment,
  CircularProgress,
  Backdrop
} from "@mui/material";
import {
  InsertDriveFile as InsertDriveFileIcon,
  Visibility as RemoveRedEyeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Publish as PublishIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  Forum as ForumIcon,
  Add as AddIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import DashboardStats from "./DashboardStats";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL, domain } from "../../variables/api";

const FormsList = (props) => {
  const [surveys, setSurveys] = useState([]);
  const [filteredSurveys, setFilteredSurveys] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [modal, setModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", id: "" });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/surveys/getallsurveysforformlists.php`
        );
        setSurveys(res.data.data.surveys);
        setFilteredSurveys(res.data.data.surveys);
        setDepartments(res.data.data.departments);
        setGrades(res.data.data.grades);
      } catch (error) {
        console.error("Error fetching surveys:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [props.pending]);

  useEffect(() => {
    const results = surveys.filter(survey =>
      survey.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      survey.owner.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredSurveys(results);
  }, [searchTerm, surveys]);

  let navigate = useNavigate();

  const handleClick = () => {
    navigate("/add-form");
  };

  const handleDuplicate = (id) => {
    setModal(true);
    setFormData((prevstate) => ({ ...prevstate, id: id }));
  };

  const handleChange = (e) => {
    let name = e.target.name;
    let value = e.target.value;
    setFormData((prevstate) => ({ ...prevstate, [name]: value }));
  };

  const handleEdit = (id) => {
    navigate(`/editforms/${id}`);
  };

  const handlePublish = (id) => {
    navigate(`/Publish/${id}`);
  };

  const handleView = (id) => {
    navigate(`/myforms/${id}`);
  };

  const handleSummary = (id) => {
    navigate(`/Summary/${id}`);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Do you want to Delete Form?",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: `YES`,
      denyButtonText: `Cancel`,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Saved!", "", "success");
        axios
          .post(`${BASE_URL}/surveys/deletesurvey.php`, { id: id })
          .then((res) => {
            props.setPending(!props.pending);
          });
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const submitHandler = (e) => {
    e.preventDefault();
    axios
      .post(`${BASE_URL}/surveys/addduplicate.php`, { formData: formData })
      .then((res) => {
        Swal.fire("Survey Added Successfully", "", "success");
      })
      .finally(() => {
        setFormData({ name: "", id: "" });
        props.setPending(!props.pending);
        setModal(false);
      });
  };

  const handleSurveyResponsesView = (id) => {
    navigate(`/Responses/${id}`);
  };

  const getSendToText = (t) => {
    const gradeText = t.filtergrades?.length > 0
      ? grades
          ?.filter((m) => t.filtergrades?.search(m.id) !== -1)
          ?.map((m) => m.grade)
          ?.join(", ")
      : "";
    
    const deptText = t.filterdept?.length > 0
      ? departments
          ?.filter((m) => t.filterdept?.search(m.id) !== -1)
          ?.map((m) => m.department)
          ?.join(", ")
      : "";

    return [gradeText, deptText].filter(Boolean).join(", ");
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      {/* Loading Overlay */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading Forms...</Typography>
        </Box>
      </Backdrop>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Dashboard
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={handleClick}
          size="large"
          sx={{ borderRadius: 2, px: 3 }}
        >
          Create New Form
        </Button>
      </Box>

      <DashboardStats surveys={surveys} recentResponses={props.recentResponses} />
      
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              All Forms
            </Typography>
            <TextField
              size="small"
              placeholder="Search forms..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Owner</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Published</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Send To</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSurveys?.map((t) => (
                  <TableRow
                    key={t.sid}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {t.sid}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{t.name}</TableCell>
                    <TableCell>{t.owner}</TableCell>
                    <TableCell>{t.date}</TableCell>
                    <TableCell>
                      <Chip 
                        label={t.published == 1 ? "Yes" : "No"} 
                        color={t.published == 1 ? "success" : "default"} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                       <Tooltip title={getSendToText(t)}>
                          <span>{getSendToText(t) || t.sendto?.replaceAll("_", " ")}</span>
                       </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Publish">
                          <IconButton onClick={() => handlePublish(t.sid)} size="small" color="primary">
                            <PublishIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton onClick={() => handleDuplicate(t.sid)} size="small">
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Responses">
                          <IconButton onClick={() => handleSurveyResponsesView(t.sid)} size="small" color="info">
                            <ForumIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Preview">
                          <IconButton onClick={() => handleView(t.sid)} size="small">
                            <RemoveRedEyeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleEdit(t.sid)} size="small" color="secondary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Summary">
                          <IconButton onClick={() => handleSummary(t.sid)} size="small" color="warning">
                            <AssessmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download CSV">
                            <IconButton 
                                href={window.location.href.search("iatseg.org") != -1 
                                    ? `https://survey.iatseg.org/backend/surveys/export.php?id=${t.sid}`
                                    : `https://survey.${domain}/backend/surveys/export.php?id=${t.sid}`
                                }
                                size="small"
                            >
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => handleDelete(t.sid)} size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSurveys.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No forms found matching your search.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={modal} onClose={() => setModal(false)}>
        <DialogTitle>Add Survey Duplicate</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            name="name"
            label="New Survey Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModal(false)}>Cancel</Button>
          <Button onClick={submitHandler} variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormsList;
